import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Loader2, CheckCircle2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const loadCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });

const releasePageLocks = () => {
  document.body.style.pointerEvents = "";
  document.body.removeAttribute("data-scroll-locked");
  document.body.style.removeProperty("overflow");
  document.documentElement.style.removeProperty("overflow");
};

const openRazorpay = (order, customerName) =>
  new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: order.pub_name,
      description: "Aivigo",
      prefill: { name: customerName },
      notes: { payment_id: order.payment_id },
      config: {
        display: {
          blocks: { upi: { name: "Pay via UPI", instruments: [{ method: "upi" }] } },
          sequence: ["block.upi"],
          preferences: { show_default_blocks: true },
        },
      },
      handler: resolve,
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
    });
    rzp.on("payment.failed", (r) => reject(new Error(r?.error?.description || "Payment failed")));
    rzp.open();
  });

/** Creates a server order, collects payment (Razorpay UPI or mock) and verifies it server-side. */
export default function PaymentDialog({ open, onOpenChange, amount, orderBody, onSuccess, customerName, label = "Pay", note }) {
  const [stage, setStage] = useState("idle");

  const pay = async () => {
    setStage("processing");
    try {
      const { data: order } = await api.post("/payments/order", orderBody);
      let proof = { order_id: order.order_id };
      if (order.mode === "razorpay") {
        await loadCheckout();
        // Close our dialog first so its focus trap / pointer-events lock can't block Razorpay's inputs (iOS keyboard).
        onOpenChange(false);
        await new Promise((r) => setTimeout(r, 350));
        releasePageLocks();
        let r;
        try {
          r = await openRazorpay(order, customerName);
        } finally {
          releasePageLocks();
        }
        proof = { order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature };
        const { data } = await api.post("/payments/verify", proof);
        setStage("idle");
        onSuccess?.(data);
        return;
      } else {
        await new Promise((r) => setTimeout(r, 800));
      }
      const { data } = await api.post("/payments/verify", proof);
      setStage("done");
      setTimeout(() => {
        setStage("idle");
        onSuccess?.(data);
      }, 700);
    } catch (e) {
      setStage("idle");
      toast.error(e?.response ? errMsg(e, "Payment failed") : e.message);
    }
  };

  const close = () => stage !== "processing" && onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="border-white/10 bg-[#131822] text-white sm:max-w-md" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CreditCard className="h-5 w-5 text-violet-400" /> Confirm payment
          </DialogTitle>
          <DialogDescription className="text-slate-400">{note || "Pay securely with UPI."}</DialogDescription>
        </DialogHeader>
        {stage !== "done" ? (
          <div className="space-y-5 pt-2">
            <div className="rounded-xl border border-white/10 bg-black/30 p-4">
              <p className="text-xs uppercase tracking-widest text-slate-400">Amount</p>
              <p className="mt-1 text-3xl font-black" data-testid="payment-amount">₹{amount}</p>
            </div>
            <Button
              onClick={pay}
              disabled={stage === "processing"}
              data-testid="payment-confirm-button"
              className="w-full bg-gradient-to-r from-violet-500 to-pink-500 py-6 font-bold text-white hover:opacity-90"
            >
              {stage === "processing" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : <>{label} · ₹{amount}</>}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10" data-testid="payment-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-400" />
            <p className="mt-3 text-lg font-semibold">Payment successful</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
